/**
 * Hook useDealPhases - CRUD de Fases do Pipeline CRM
 * 
 * Gerencia criação de fases diretamente do Kanban
 * Segue arquitetura de segurança tenant v2.0
 * 
 * Versão: 1.0.0
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

// Logger do módulo
const logger = createLogger('DealPhases');

/**
 * Hook para gerenciar fases de pipeline CRM
 */
export function useDealPhases(pipelineId: string | null) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  // Mutation para criar fase
  const createPhase = useMutation({
    mutationFn: async ({ 
      name, 
      color, 
      probability_percent = 50, 
      is_won = false, 
      is_lost = false 
    }: { 
      name: string; 
      color: string; 
      probability_percent?: number;
      is_won?: boolean;
      is_lost?: boolean;
    }) => {
      if (!currentTenant?.id || !pipelineId) {
        throw new Error('Tenant ou Pipeline não encontrado');
      }

      // v1.2.0: Validação de unicidade aplicada SOMENTE para Ganho.
      // Perda passa a aceitar múltiplas fases (cada uma representa um motivo de perda),
      // permitindo categorizar a razão sem precisar de tabela/campo extra.
      if (is_won) {
        const { data: existingPhase } = await supabase
          .from('crm_pipeline_phases')
          .select('id, name')
          .eq('pipeline_id', pipelineId)
          .eq('tenant_id', currentTenant.id)
          .eq('is_won', true)
          .maybeSingle();

        if (existingPhase) {
          throw new Error(
            `Já existe uma fase do tipo "Ganho" neste pipeline: "${existingPhase.name}". ` +
            `Cada pipeline pode ter apenas uma fase de Ganho.`
          );
        }
      }

      // Busca maior sort_order atual (excluindo fases de ganho/perda)
      const { data: phases } = await supabase
        .from('crm_pipeline_phases')
        .select('sort_order, is_won, is_lost')
        .eq('pipeline_id', pipelineId)
        .eq('tenant_id', currentTenant.id)
        .order('sort_order', { ascending: false });

      // Encontra posição antes das fases de ganho/perda
      const normalPhases = phases?.filter(p => !p.is_won && !p.is_lost) || [];
      const wonLostPhases = phases?.filter(p => p.is_won || p.is_lost) || [];
      
      const insertOrder = normalPhases.length > 0 
        ? Math.max(...normalPhases.map(p => p.sort_order)) + 1 
        : 0;

      // Atualiza sort_order das fases de ganho/perda para manter no final
      if (wonLostPhases.length > 0) {
        for (let i = 0; i < wonLostPhases.length; i++) {
          const phase = wonLostPhases[i];
          await supabase
            .from('crm_pipeline_phases')
            .update({ sort_order: insertOrder + 1 + i })
            .eq('pipeline_id', pipelineId)
            .eq('tenant_id', currentTenant.id)
            .eq('sort_order', phase.sort_order);
        }
      }

      // Cria nova fase com parâmetros recebidos
      const { data, error } = await supabase
        .from('crm_pipeline_phases')
        .insert({
          pipeline_id: pipelineId,
          tenant_id: currentTenant.id,
          name,
          color,
          sort_order: insertOrder,
          probability_percent,
          is_won,
          is_lost,
        })
        .select()
        .single();

      if (error) {
        logger.error('Erro ao criar fase:', error.message);
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      // Invalida cache das fases e stats
      queryClient.invalidateQueries({ 
        queryKey: ['pipeline-phase-stats', pipelineId] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['crm-pipeline-phases', pipelineId] 
      });
      // Invalida pipelines para refletir no modal de workspaces
      queryClient.invalidateQueries({ 
        queryKey: ['workspace-pipelines'] 
      });
      toast.success('Fase criada com sucesso!');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao criar fase: ${error.message}`);
    },
  });

  return {
    createPhase,
  };
}
